import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../use-web-socket';
import type { ParsedEventData } from '../use-web-socket/types';
import { EVENT_IDENTIFIER_KEYS_TO_INVALIDATE_ON } from './constants';
import type { EventContext, PigelloEventListener } from './types';
import { getModelName } from './utils';
import { useSession } from '@/providers/session-provider';

export const useEventSubscription = (
  specialListeners: PigelloEventListener
) => {
  const queryClient = useQueryClient();
  const {
    data: {
      session: { userId },
    },
  } = useSession();

  // Handled in onMessage rather than via state so that messages arriving back to
  // back aren't collapsed into one render.
  const onMessage = (message: ParsedEventData) => {
    if (message.invoke_type !== 0) {
      return;
    }

    for (const event of message.pigello_events) {
      if (event.invoked_by_user_id === userId) {
        continue;
      }

      const modelName = getModelName(event.content_type);
      const objectId = event.object_id;

      const context: EventContext = { queryClient };
      // Run special listeners
      specialListeners(event, context);

      const identifier = event.event_identifier.split('.').at(-1);
      if (
        !identifier ||
        !EVENT_IDENTIFIER_KEYS_TO_INVALIDATE_ON.includes(identifier)
      ) {
        continue;
      }

      if (modelName && objectId) {
        queryClient.invalidateQueries({
          queryKey: [modelName, objectId],
        });
      }
    }
  };

  useWebSocket({ onMessage });
};
